import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Chip,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material'
import {
  Search,
  Clear,
  FilterList,
  Description,
  Business,
  History
} from '@mui/icons-material'
import { useQuery } from 'react-query'
import axios from 'axios'

const SearchBar = ({ 
  onSearch, 
  onFilterChange, 
  placeholder = "Buscar procedimientos...",
  filters = {},
  showFilters = true 
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [recentSearches, setRecentSearches] = useState([])

  // Cargar búsquedas recientes del localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sgc_recent_searches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Error loading recent searches:', error)
      }
    }
  }, [])

  // Query para sugerencias de búsqueda
  const { data: suggestions = [] } = useQuery(
    ['search-suggestions', searchTerm],
    async () => {
      if (searchTerm.length < 2) return []

      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/procedimientos`, {
        params: {
          search: searchTerm,
          limit: 8
        },
        headers: { Authorization: `Bearer ${token}` }
      })

      // Manejar respuestas que pueden venir como array o como objeto con propiedad procedimientos
      const procedimientos = Array.isArray(response.data)
        ? response.data
        : (response.data.procedimientos || [])

      return procedimientos.map(proc => ({
        id: proc.id,
        codigo: proc.codigo,
        titulo: proc.titulo,
        gestion_nombre: proc.gestion_nombre,
        type: 'procedimiento'
      }))
    },
    {
      enabled: searchTerm.length >= 2,
      staleTime: 30 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  )

  const handleSearch = (value = searchTerm) => {
    if (value.trim()) {
      // Guardar en búsquedas recientes
      const newRecentSearches = [
        value,
        ...recentSearches.filter(item => item !== value)
      ].slice(0, 5)
      
      setRecentSearches(newRecentSearches)
      localStorage.setItem('sgc_recent_searches', JSON.stringify(newRecentSearches))
      
      onSearch(value.trim())
    }
    setShowSuggestions(false)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClear = () => {
    setSearchTerm('')
    onSearch('')
    setShowSuggestions(false)
  }

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.codigo + ' - ' + suggestion.titulo)
    handleSearch(suggestion.codigo)
  }

  const handleRecentSearchClick = (term) => {
    setSearchTerm(term)
    handleSearch(term)
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('sgc_recent_searches')
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={() => setShowSuggestions(true)}
        placeholder={placeholder}
        variant="outlined"
        size="medium"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searchTerm && (
                <IconButton size="small" onClick={handleClear}>
                  <Clear />
                </IconButton>
              )}
              {showFilters && (
                <IconButton 
                  size="small" 
                  onClick={() => console.log('Show filters')}
                  color={Object.keys(filters).length > 0 ? 'primary' : 'default'}
                >
                  <FilterList />
                </IconButton>
              )}
            </InputAdornment>
          ),
          sx: {
            borderRadius: 3,
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'grey.50'
            }
          }
        }}
      />

      {/* Panel de sugerencias */}
      {showSuggestions && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 400,
            overflow: 'auto',
            mt: 1,
            boxShadow: 3
          }}
          onMouseDown={(e) => e.preventDefault()} // Prevenir que se pierda el foco
        >
          {/* Sugerencias de procedimientos */}
          {suggestions.length > 0 && (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Procedimientos
                </Typography>
              </Box>
              <List dense>
                {suggestions.map((suggestion) => (
                  <ListItem
                    key={suggestion.id}
                    button
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <ListItemIcon>
                      <Description color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${suggestion.codigo} - ${suggestion.titulo}`}
                      secondary={suggestion.gestion_nombre}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.9rem'
                      }}
                      secondaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.8rem'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Búsquedas recientes */}
          {recentSearches.length > 0 && searchTerm.length < 2 && (
            <>
              {suggestions.length > 0 && <Divider />}
              <Box sx={{ 
                p: 2, 
                pb: 1, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Búsquedas recientes
                </Typography>
                <Typography 
                  variant="caption" 
                  color="primary" 
                  sx={{ cursor: 'pointer' }}
                  onClick={clearRecentSearches}
                >
                  Limpiar
                </Typography>
              </Box>
              <List dense>
                {recentSearches.map((term, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleRecentSearchClick(term)}
                  >
                    <ListItemIcon>
                      <History color="action" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={term}
                      primaryTypographyProps={{
                        fontSize: '0.9rem'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Estado vacío */}
          {suggestions.length === 0 && recentSearches.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchTerm.length >= 2 
                  ? 'No se encontraron resultados' 
                  : 'Escribe para buscar procedimientos'
                }
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Filtros activos */}
      {Object.keys(filters).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {Object.entries(filters).map(([key, value]) => (
            value && (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                size="small"
                onDelete={() => onFilterChange && onFilterChange(key, null)}
                color="primary"
                variant="outlined"
              />
            )
          ))}
        </Box>
      )}

      {/* Overlay para cerrar sugerencias */}
      {showSuggestions && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1299
          }}
          onClick={() => setShowSuggestions(false)}
        />
      )}
    </Box>
  )
}

export default SearchBar